'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { getSocket, disconnectSocket, Socket } from '@/lib/socket';
import { useAuthStore } from '@/store/use-auth-store';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  reconnect: () => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  reconnect: () => {},
});

export const useSocketContext = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isAuthLoading } = useAuthStore();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const initializeSocket = () => {
    try {
      const socketInstance = getSocket();
      setSocket(socketInstance);
      socketRef.current = socketInstance;

      socketInstance.on('connect', () => {
        setIsConnected(true);
      });

      socketInstance.on('disconnect', () => {
        setIsConnected(false);
      });
    } catch (error) {
      console.error('Failed to initialize socket:', error);
    }
  };

  const reconnect = () => {
    if (socketRef.current) {
      disconnectSocket();
      setSocket(null);
      socketRef.current = null;
      setIsConnected(false);
    }
    if (isAuthenticated) {
      initializeSocket();
    }
  };

  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated) {
      if (socketRef.current) {
        disconnectSocket();
        setSocket(null);
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    initializeSocket();

    return () => {
      if (socketRef.current) {
        disconnectSocket();
        setSocket(null);
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [isAuthenticated, isAuthLoading]);

  return (
    <SocketContext.Provider value={{ socket, isConnected, reconnect }}>
      {children}
    </SocketContext.Provider>
  );
};


